import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Tabs, Typography, Empty } from 'antd';
import './styles.css';

// 虚拟滚动配置
const ITEM_HEIGHT = 36; // 每项高度
const OVERSCAN_COUNT = 5; // 上下额外渲染的项数

const { TabPane } = Tabs;
const { Text } = Typography;

/**
 * 虚拟列表组件 - 只渲染可见区域的项
 */
interface VirtualListProps {
  items: Node[];
  renderItem: (node: Node, index: number) => React.ReactNode;
  height: number;
  className?: string;
}

const VirtualList: React.FC<VirtualListProps> = ({
  items,
  renderItem,
  height,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const totalHeight = items.length * ITEM_HEIGHT;

  // 计算可见范围
  const visibleRange = useMemo(() => {
    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN_COUNT
    );
    const visibleCount = Math.ceil(height / ITEM_HEIGHT) + 2 * OVERSCAN_COUNT;
    const endIndex = Math.min(items.length - 1, startIndex + visibleCount);
    return { startIndex, endIndex };
  }, [scrollTop, height, items.length]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  // 渲染可见项
  const visibleItems = useMemo(() => {
    const result: React.ReactNode[] = [];
    for (let i = visibleRange.startIndex; i <= visibleRange.endIndex; i++) {
      const item = items[i];
      if (item) {
        result.push(
          <div
            key={item.id}
            style={{
              position: 'absolute',
              top: i * ITEM_HEIGHT,
              left: 0,
              right: 0,
              height: ITEM_HEIGHT,
            }}
          >
            {renderItem(item, i)}
          </div>
        );
      }
    }
    return result;
  }, [items, visibleRange, renderItem]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        height,
        overflow: 'auto',
        position: 'relative',
      }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        {visibleItems}
      </div>
    </div>
  );
};

interface Node {
  id: string;
  type: string;
  text?: any;
  properties?: any;
  [key: string]: any;
}

interface NodeListPanelProps {
  graphData?: { nodes: any[]; edges: any[] };
  lfRef?: React.MutableRefObject<any>;
  onHighlightNode?: (nodeId: string) => void;
  rootNodeId?: string | null;
  isSubgraph?: boolean;
}

const isStateNode = (node: Node): boolean => {
  return node?.type === 'state' || node?.properties?.isState === true;
};

const isValueNode = (node: Node): boolean => {
  return node?.type === 'value' || node?.properties?.isValue === true;
};

const isGroupNode = (node: Node): boolean => {
  return node?.type === 'dynamic-group' || node?.type === 'group';
};

const getNodeColor = (node: Node): string => {
  if (node?.type === 'rect') {
    return '#70E483';
  }
  if (node?.type === 'polygon') {
    return '#3BC3FF';
  }
  if (isGroupNode(node)) {
    return '#13c2c2';
  }
  return '#BFBFBF';
};

const NodeListPanel: React.FC<NodeListPanelProps> = ({
  graphData = { nodes: [], edges: [] },
  lfRef,
  onHighlightNode,
  rootNodeId,
  isSubgraph = false,
}) => {
  const [activeTab, setActiveTab] = useState('objects');

  // 获取节点显示名称
  const getDisplayName = useCallback((node: Node): string => {
    // 优先显示有意义的名称，而不是ID
    let displayName =
      node.text?.value ||
      node.text ||
      node.properties?.name ||
      node.properties?.text ||
      '';

    // 如果显示名称为空、是节点ID（长UUID格式）、或包含长连字符格式，则使用默认名称
    const isUuidLike =
      displayName && displayName.includes('-') && displayName.length > 10;
    const isEmpty = !displayName || displayName.trim() === '';
    const isNodeId = displayName === node.id;

    if (isEmpty || isNodeId || isUuidLike) {
      // 根据节点类型生成默认名称
      const nodeTypeConfig = {
        rect: '对象',
        polygon: '过程',
        circle: '属性',
        diamond: '菱形',
        'dynamic-group': '分组',
        group: '分组',
      };
      const typeName =
        nodeTypeConfig[node.type as keyof typeof nodeTypeConfig] || '节点';
      return `新${typeName}`;
    }

    return displayName;
  }, []);

  // 过滤对象节点 (rect类型) - 使用 useMemo 缓存
  const objectNodes = useMemo(() => {
    return graphData.nodes
      .filter(
        node =>
          (node.type === 'rect' || isGroupNode(node)) &&
          !isStateNode(node) &&
          !isValueNode(node)
      )
      .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
  }, [graphData.nodes, getDisplayName]);

  // 过滤过程节点 (polygon类型) - 使用 useMemo 缓存
  const processNodes = useMemo(() => {
    return graphData.nodes
      .filter(
        node =>
          node.type === 'polygon' && !isStateNode(node) && !isValueNode(node)
      )
      .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
  }, [graphData.nodes, getDisplayName]);

  // 定位到指定节点并高亮
  const focusOnNode = useCallback(
    (nodeId: string) => {
      if (!lfRef?.current) {
        console.warn('LogicFlow实例不存在');
        return;
      }

      try {
        // 使用高亮功能（类似搜索时的聚焦效果）
        if (onHighlightNode) {
          onHighlightNode(nodeId);
        } else {
          // 备用方案：直接定位和选择
          lfRef.current.clearSelectElements();
          lfRef.current.selectElementById(nodeId);
          lfRef.current.focusOn(nodeId);
        }

        // 限制缩放比例，避免过度放大
        setTimeout(() => {
          const currentTransform = lfRef.current?.getTransform();
          const currentScale =
            currentTransform?.SCALE_X || currentTransform?.scale || 1;

          if (currentScale > 1.5) {
            lfRef.current?.zoom(1.5 / currentScale);
          } else if (currentScale < 0.8) {
            lfRef.current?.zoom(0.8 / currentScale);
          }
        }, 50);

        console.log(`✅ 成功定位并高亮节点: ${nodeId}`);
      } catch (error) {
        console.warn('定位节点失败:', error);
      }
    },
    [lfRef, onHighlightNode]
  );

  // 渲染节点列表项 - 使用 useCallback 避免重复创建
  const renderNodeItem = useCallback(
    (node: Node) => (
      <div
        className="node-list-item"
        onClick={() => focusOnNode(node.id)}
        style={{
          height: ITEM_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          cursor: 'pointer',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <div className="node-item-content">
          <div
            className="node-type-indicator"
            style={{
              backgroundColor: getNodeColor(node),
            }}
          />
          <Text className="node-name" title={getDisplayName(node)}>
            {getDisplayName(node)}
          </Text>
        </div>
      </div>
    ),
    [focusOnNode, getDisplayName]
  );

  return (
    <div className="node-list-panel">
      {/* <div className="node-list-header">
        <h3>节点列表</h3>
      </div>
       */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="node-list-tabs"
        size="small"
      >
        <TabPane
          tab={
            <span>
              {/* <NodeIndexOutlined /> */}
              对象 ({objectNodes.length})
            </span>
          }
          key="objects"
        >
          {objectNodes.length > 0 ? (
            <VirtualList
              items={objectNodes}
              renderItem={renderNodeItem}
              height={400}
              className="node-list"
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无对象节点"
              className="empty-state"
            />
          )}
        </TabPane>

        <TabPane
          tab={
            <span>
              {/* <BranchesOutlined /> */}
              过程 ({processNodes.length})
            </span>
          }
          key="processes"
        >
          {processNodes.length > 0 ? (
            <VirtualList
              items={processNodes}
              renderItem={renderNodeItem}
              height={400}
              className="node-list"
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无过程节点"
              className="empty-state"
            />
          )}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default NodeListPanel;
